import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
} from 'reactstrap';
import { fetchGroupOrder } from 'redux/GroupOrders/GroupOrdersActions';
import GroupPaymentForm from 'components/GroupOrders/GroupPaymentForm';
import { getPlanById } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const GroupPayment = (props) => {
  const { groupOrderId } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (groupOrderId) {
      dispatch(fetchGroupOrder(groupOrderId));
    }
  }, [groupOrderId]);

  const { data: order, loading, error } = props.fetchGroupOrder;

  if (loading) {
    return (
      <div className="bg-gradient-primary" style={{ minHeight: '100vh' }}>
        <Container className="pt-5">
          <Row className="justify-content-center">
            <Col lg="6" className="text-center pt-5">
              <Spinner color="white" />
              <p className="text-white mt-3">Loading order details...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-primary" style={{ minHeight: '100vh' }}>
        <Container className="pt-5">
          <Row className="justify-content-center">
            <Col lg="6" className="pt-5">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }} />
                  <h3 className="mt-3">Unable to Load Order</h3>
                  <p className="text-muted">{error.error || 'This payment link may be invalid or expired.'}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!order) return null;

  if (order.status === 'paid') {
    return (
      <div className="bg-gradient-primary" style={{ minHeight: '100vh' }}>
        <Container className="pt-5">
          <Row className="justify-content-center">
            <Col lg="6" className="pt-5">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }} />
                  <h3 className="mt-3">Payment Already Completed</h3>
                  <p className="text-muted">
                    This order has already been paid. The coordinator should have received the access codes.
                  </p>
                  <p className="text-muted">
                    Log in or register with <strong>{order.contactEmail}</strong> to manage your codes.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const options = {
    mode: 'payment',
    amount: Math.round(order.totalAmount * 100),
    currency: 'usd',
    payment_method_types: ['card', 'link'],
    appearance: {},
  };

  return (
    <div className="bg-gradient-primary" style={{ minHeight: '100vh' }}>
      <Container className="pt-5 pb-5">
        <Row className="justify-content-center">
          <Col lg="4" className="pt-3">
            <div className="text-center mb-4">
              <img
                src={require('assets/img/brand/pmr-logo.png')}
                alt={`${getSpecialty(order?.specialty).name} Prep`}
                style={{ height: '50px' }}
              />
            </div>
            <Card className="shadow">
              <CardBody>
                <h4 className="text-center mb-3">Order Summary</h4>
                <hr />
                <div className="mb-2">
                  <span className="text-muted">Program</span>
                  <br />
                  <strong>{order.programName}</strong>
                </div>
                {order.year && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Year</span>
                    <span>{order.year}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Contact</span>
                  <span>{order.contactName}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Email</span>
                  <span>{order.contactEmail}</span>
                </div>
                {order.items ? (
                  <>
                    {order.items.map((item, idx) => (
                      <div key={idx} className="d-flex justify-content-between mb-2">
                        <span className="text-muted">{getPlanById(item.planId)?.name || item.planId}</span>
                        <span>{item.quantity} seat{item.quantity !== 1 ? 's' : ''} — ${item.pricePerSeat}/ea</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Plan</span>
                      <span>{getPlanById(order.planId)?.months || '—'} Months Access</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Seats</span>
                      <span>{order.classSize}</span>
                    </div>
                    {(() => {
                      const plan = getPlanById(order.planId);
                      const regularPrice = plan?.price;
                      const basePrice = order.basePrice != null
                        ? order.basePrice
                        : order.discountPercent < 100
                          ? +(order.pricePerSeat / (1 - order.discountPercent / 100)).toFixed(2)
                          : 0;
                      const isDiscounted = regularPrice && basePrice < regularPrice;
                      return (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Base Price</span>
                          <span>
                            ${basePrice}
                            {isDiscounted && (
                              <span className="text-muted ml-1" style={{ textDecoration: 'line-through' }}>
                                ${regularPrice}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const basePrice = order.basePrice != null
                        ? order.basePrice
                        : order.discountPercent < 100
                          ? +(order.pricePerSeat / (1 - order.discountPercent / 100)).toFixed(2)
                          : 0;
                      const showStrike = order.discountPercent > 0 && basePrice > order.pricePerSeat;
                      return (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Price per Seat</span>
                          <span>
                            ${order.pricePerSeat}
                            {showStrike && (
                              <span className="text-muted ml-1" style={{ textDecoration: 'line-through' }}>
                                ${basePrice}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })()}
                  </>
                )}
                {order.discountPercent > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Group Discount</span>
                    <span className="text-success">{order.discountPercent}% off</span>
                  </div>
                )}
                {order.items && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Total Seats</span>
                    <span>{order.classSize}</span>
                  </div>
                )}
                <hr />
                <div className="d-flex justify-content-between">
                  <strong>Total</strong>
                  <strong className="text-primary" style={{ fontSize: '1.2rem' }}>
                    ${order.totalAmount}
                  </strong>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="6" className="pt-3">
            <Card className="shadow">
              <CardBody>
                <h4 className="text-center mb-3">Complete Payment</h4>
                <p className="text-muted text-center" style={{ fontSize: '0.85rem' }}>
                  After payment, {order.classSize} access codes will be generated for your residents.
                </p>
                <Elements stripe={stripePromise} options={options}>
                  <GroupPaymentForm groupOrderId={groupOrderId} order={order} />
                </Elements>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  fetchGroupOrder: state.fetchGroupOrder,
});

export default connect(mapStateToProps)(GroupPayment);
